import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider';

import {
  beginSignOut,
  clearLegacyTokens,
  clearUserData,
  endSignOut,
  postChallengeAnswer,
  postForgotPassword,
  postResetPassword,
  postSignIn,
  postSignOut,
  refreshServerSession,
  SignInStep,
} from './api/sessionAPI';
import { poolData } from './utils/cognitoUtils';
import { formatPhoneNumber } from './utils/phoneNumberUtils';

export const cognitoClient = new CognitoIdentityProviderClient({
  region: poolData.Region,
});

/**
 * HA-445: signs in through the API, which keeps the tokens and sets the HttpOnly session cookie;
 * the password goes to the API, never to Cognito from the browser. With `newPassword`, a
 * NEW_PASSWORD_REQUIRED challenge is answered straight away (the temporary-password form asks for
 * the new password before signing in). Throws SignInError when refused.
 */
export const signIn = async (
  username: string,
  password: string,
  newPassword?: string
): Promise<SignInStep> => {
  const step = await postSignIn(username, password);

  if (
    newPassword &&
    step.status === 'challenge' &&
    step.challengeName === 'NEW_PASSWORD_REQUIRED'
  ) {
    return postChallengeAnswer(step.challengeId, newPassword);
  }

  return step;
};

/**
 * Tells the API the user is still here, which extends the session's idle limit (never its
 * absolute one). False when the session has ended: callers treat that as signed out.
 */
export const refreshSession = (): Promise<boolean> => refreshServerSession();

const SIGN_OUT_ATTEMPTS = 3;

/**
 * Ends the session on the API (which clears the cookie), with the user's cached data and any token
 * cookies left from before. Retried, because a sign-out that silently fails leaves the session
 * live (on a shared computer, for the next person). False if the API still couldn't be reached.
 */
export const signOut = async (): Promise<boolean> => {
  beginSignOut();
  let ok = await postSignOut();

  for (let attempt = 1; !ok && attempt < SIGN_OUT_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
    ok = await postSignOut();
  }
  clearLegacyTokens();
  clearUserData();
  if (!ok) {
    // Still signed in as far as anyone can tell: session handling goes back to normal.
    endSignOut();
  }

  return ok;
};

export const signUp = async (
  email: string,
  password: string,
  role: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
  dateOfBirth: string,
  practiceName: string | null
) => {
  if (!practiceName) {
    throw new Error('Practice name is required');
  }

  const finalPracticeName = `${practiceName}#${email}`;

  const userAttributes = [
    {
      Name: 'email',
      Value: email,
    },
    {
      Name: 'given_name',
      Value: firstName,
    },
    {
      Name: 'family_name',
      Value: lastName,
    },
    {
      Name: 'phone_number',
      Value: formatPhoneNumber(phoneNumber),
    },
    {
      Name: 'birthdate',
      Value: dateOfBirth,
    },
  ];

  // HA-441: role and practice are NOT user attributes the browser may write - the
  // app client can't write custom:* (any signed-in user could otherwise rewrite
  // their own role/practice). They travel as ClientMetadata; the PreSignUp trigger
  // validates them and the user Lambda assigns them server-side on confirmation.
  const params = {
    ClientId: poolData.ClientId,
    Username: email,
    Password: password,
    UserAttributes: userAttributes,
    ClientMetadata: {
      role,
      practiceName: finalPracticeName,
    },
  };

  try {
    const command = new SignUpCommand(params);
    const response = await cognitoClient.send(command);

    return response;
  } catch (error) {
    console.error('Error signing up: ', error);
    throw error;
  }
};

export const confirmSignUp = async (username: string, code: string) => {
  const params = {
    ClientId: poolData.ClientId,
    Username: username,
    ConfirmationCode: code,
  };

  try {
    const command = new ConfirmSignUpCommand(params);

    await cognitoClient.send(command);

    return true;
  } catch (error) {
    console.error('Error confirming sign up: ', error);
    throw error;
  }
};

/**
 * Sends a password reset code. HA-456: through the API, which answers the same whether or not the
 * account exists (HA-457: the page used to probe with a sign-in attempt first, which told anyone
 * which addresses have accounts). Throws only when the user should wait or try again.
 */
export const requestPasswordReset = async (email: string) => {
  await postForgotPassword(email);

  return true;
};

/**
 * Answers the MFA step of a sign-in with the authenticator code. A challenge takes one answer:
 * after a wrong code, sign in again for a new one. Throws SignInError when refused.
 */
export const respondToMfaChallenge = (challengeId: string, code: string): Promise<SignInStep> =>
  postChallengeAnswer(challengeId, code);

/** The sign-in needs an authenticator code next. */
export const requiresMfa = (
  step: SignInStep | undefined
): step is Extract<SignInStep, { status: 'challenge' }> =>
  step?.status === 'challenge' && step.challengeName === 'SOFTWARE_TOKEN_MFA';

/**
 * Sets a new password with the emailed code. HA-456: through the API, which ends every session of
 * the user (a lost device, whoever had the old password). No sign-in here: the page goes back to
 * the sign-in form, where the user signs in (and answers MFA) with the new password.
 */
export const verifyResetCode = async (
  email: string,
  confirmationCode: string,
  newPassword: string
) => {
  await postResetPassword(email, confirmationCode, newPassword);

  return true;
};
